import {Component, OnDestroy, OnInit} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable, Subscription, combineLatest } from "rxjs";
import {Collection, CollectionDetails, DeviceStatus, EntityStatusResponse, Point} from "src/app/models/dashboard";
import { BackendService } from "src/app/services/backend/backend.service";
import { SiteFilterService } from "src/app/services/site-filter.service";
import { StoreService } from "src/app/services/store.service";
import { RSocket, RSocketClient } from "rsocket-core";
import { BINARY, SELECTED_SITE } from "src/app/config/constants";
import { UtilsService } from "src/app/services/utils/util.service";
import { OAuthService } from 'angular-oauth2-oidc';
import {EntityParam, EntityType} from "../../../models/api/entity-type";
import {AuthorizationService} from "../../../services/authorization.service";
import { SelectedSite } from "src/app/models/site";

@Component({
    selector: "cc-collection-details-view-wrapper",
    templateUrl: "./details-view-wrapper.component.html",
    styleUrls: ["./details-view-wrapper.component.scss"],
    standalone: false,
})
export class CollectionDetailsViewWrapperComponent implements OnInit, OnDestroy {
    collectionDetails: CollectionDetails;

    collectionPointsArray: Point[];
	pointsObj: { [key: string]: { [key: string]: Point[] } } = {};
    username: string = "";
    isOverrideResetOn: boolean = false;
	userHasWritePermission$: Observable<boolean>;
    selectedSite: SelectedSite;
    entityStatus: { [key: string]: DeviceStatus } = {};

    collectionId: string;
    private mainSubscription: Subscription;
    private polledPointsValuesSubscription: Subscription;
    private rSocketClient: RSocketClient;

    constructor(
        private backendService: BackendService,
        private activatedRoute: ActivatedRoute,
        private siteFilterService: SiteFilterService,
        private storeService: StoreService,
        private utilsService: UtilsService,
        private authService: OAuthService,
        private router: Router,
        private authorizationService: AuthorizationService,
        private util: UtilsService
    ) {}

    ngOnInit(): void {
        const pathnameArray = window.location.pathname.split('/');
		if (pathnameArray[1] === 'customers' && pathnameArray[5] === 'equipments') {
			this.router.navigate([`/collections/${pathnameArray[6]}`]);
		}

        this.userHasWritePermission$ = this.storeService.observable;

		this.storeService.canRemoveOverrideChanged$.subscribe((value) => {
			this.isOverrideResetOn = value;
		})

        this.username = this.authorizationService.getUsername();

        this.mainSubscription = combineLatest([
            this.activatedRoute.paramMap,
        ]).subscribe(([routeParamMap]) => {
            const selectedSite = this.util.parseStringToJson(sessionStorage.getItem(SELECTED_SITE));
            this.selectedSite = selectedSite;
            let siteId = selectedSite?.value;
            this.collectionId = routeParamMap["params"]["id"];

            if (!siteId) {
                return;
            }

            this.backendService
                .getCollectionDetails(siteId, this.username, this.collectionId, selectedSite?.customerId)
                .subscribe((response) => {
                    this.collectionDetails = response;
                    this.loadEntityStatus(this.collectionId);
                    this.collectionPointsArray = [
                        ...this.collectionDetails.setpointsAndParametersDatapointsList,
                        ...this.collectionDetails
                            .commandsDatapointsList,
                        ...this.collectionDetails
                            .sensorsAndStatusDatapointsList,
                        ...this.collectionDetails.alarmsDatapointsList
                    ];

					this.pointsObj = { [this.collectionDetails.label] : { [this.collectionDetails.label]: this.collectionPointsArray }};
					this.callKpiPolling(siteId, this.collectionPointsArray.map(point => point.pointUUID));
                });
        });
    }

    ngOnDestroy() {
        if (this.mainSubscription) {
            this.mainSubscription.unsubscribe();
        }

        if (this.polledPointsValuesSubscription) {
            this.polledPointsValuesSubscription.unsubscribe();
        }

		this.closeRsocket();
    }

    callKpiPolling(siteId: string, datapointIdListFilter: string[]) {
        ////////
        // Create an instance of a rSocketClient
        this.rSocketClient = this.backendService.getRSocketClient();
        // Open the connection
        this.polledPointsValuesSubscription = this.rSocketClient
            .connect()
            .subscribe({
                onComplete: (socket: RSocket) => {
                    let requestStream = "request-stream-get-datapoint-list2";
                    socket
                        .requestStream({
                            data: {
                                siteId: siteId,
                                datapointIdListFilter: datapointIdListFilter,
                                //TODO get token from authentication
                                jwtToken: this.authService.getIdToken(),
                                interaction: "Request",
                            }, // null is a must if it does not include a message payload, else the Spring server side will not be matched.
                            metadata:
                                String.fromCharCode(requestStream.length) +
                                requestStream,
                        })
                        .subscribe({
                            onComplete: () => console.log("complete"),
                            onError: (error) => {
                                console.log(
                                    "Connection has been closed due to:: " +
                                        error
                                );
                            },
                            onNext: (payload) => {
                                this.mapCollectionPoints(payload.data);
                            },
                            onSubscribe: (subscription) => {
                                subscription.request(1000000);
                            },
                        });
                },
                onError: (error) => {
                    console.log(
                        "Connection has been refused due to:: " + error
                    );
                },
                onSubscribe: (cancel) => {
                    /* call cancel() to abort */
                },
            });
    }

    mapCollectionPoints(point: any) {
        //do not update locked values;
        if (
            Object.keys(this.storeService.getLockedPoints()).includes(point.id)
        ) {
            return;
        }

        let pointToUpdateArr = this.collectionPointsArray.filter(
            (collectionPoint) => collectionPoint.pointUUID === point.id
        );

        if (pointToUpdateArr?.length) {
            pointToUpdateArr = pointToUpdateArr.map(pointToUpdate => {
                pointToUpdate.extensions = point.extensions;
                pointToUpdate.presentValue = point.lastRecordedValue;
                pointToUpdate.lastRecordedValueMask = point.lastRecordedValueMask;
                pointToUpdate.signalType = point.signalType;
                pointToUpdate.type = point.type;
                pointToUpdate.units = point.units;
                pointToUpdate.isWritable = this.isPointWritable(pointToUpdate);
                pointToUpdate.isOverridden = point.isOverridden;
                pointToUpdate.activeOverrides = point.activeOverrides;

                if (point.signalType === BINARY) {
                    pointToUpdate.presentValue = point.lastRecordedValue
                        ? true
                        : false;
                }
                return pointToUpdate;
            });
        }
    }

    private loadEntityStatus(collectionId: string) {
        const params: EntityParam = {
            siteId: this.selectedSite?.value,
            customerId: this.selectedSite?.customerId,
            entityIds: [collectionId],
            entityType: EntityType.COLLECTION,
        }
        this.backendService.getEntitiesStatus(params).subscribe((response: EntityStatusResponse) => this.entityStatus = response.entityStatus);
    }


    private isPointWritable(point: Point) {
        const pointMainClass = point.class.split("_").pop();
        if (
            ["Command", "Parameter", "Setpoint", "Limit"].includes(
                pointMainClass
            )
        )
            return true;
        if (["Alarm", "Sensor", "Status"].includes(pointMainClass))
            return false;
    }

    private closeRsocket() {
        if (this.rSocketClient) {
            this.rSocketClient.close();
        }
    }
}
